import { webpack } from '@dr.pogodin/react-utils';

import type * as ServerServices from 'server/services';

export * from './ai';
export * from './artist';
export * from './assets';
export * from './auth';
export * from './blog';
export * from './body-areas';
export * from './body-positions';
export * from './config';
export * from './constants';
export * from './equipment';
export * from './experience';
export * from './files';
export * from './filters';
export * from './global-state';
export * from './intents';
export * from './life-moments-framework';
export * from './modalities';
export * from './paths';
export * from './questionnaire';
export type * from './research-lib';
export * from './sessions';
export * from './transcript';
export * from './user-profile';
export * from './user-scores';
export * from './users';
export * from './validators';
export * from './video';
export * from './vimeo';

export const MDASH = '—';

/** Returns "true" if both arrays have the same elements (compared shallowly,
 *  element-wise); returns "false" otherwise. */
export function areEqualArrays<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

/**
 * Returns "true" if both arrays represent the same set (i.e. each element
 * present in the array `a` is present in the array `b`, and vice versa,
 * no matter the order, or number of repeatitions of each element);
 * otherwise returns "false".
 */
export function areEqualArraySets<T>(
  a: T[] | null | undefined,
  b: T[] | null | undefined,
): boolean {
  if (!a || !b) return !a?.length && !b?.length;

  let set = new Set(a);
  for (const item of b) if (!set.has(item)) return false;

  set = new Set(b);
  for (const item of a) if (!set.has(item)) return false;

  return true;
}

export function assertedNumber(number: null | number | undefined): number {
  if (number === null || number === undefined) throw Error('Not a number');
  return number;
}

export function assertedString(string: null | string | undefined): string {
  if (string === null || string === undefined) throw Error('Not a string');
  return string;
}

/** Pretty-prints given number of bytes. */
export function formatByteSize(numBytes: number): string {
  let res: number = numBytes;

  if (res < 1024) return `${res}B`;

  res /= 1024;
  if (res < 1024) return `${res.toFixed(1)}KB`;

  res /= 1024;
  if (res < 1024) return `${res.toFixed(1)}MB`;

  res /= 1024;
  if (res < 1024) return `${res.toFixed(1)}GB`;

  res /= 1024;
  return `${res.toFixed(1)}TB`;
}

/**
 * Formats a time interval, given in minutes, as MM:SS value.
 */
export function formatMinsAsMMSS(mins: number): string {
  let mm = Math.floor(mins).toFixed();
  if (mm.length === 1) mm = `0${mm}`;
  let ss = (60 * (mins % 1)).toFixed();
  if (ss.length === 1) ss = `0${ss}`;
  return `${mm}:${ss}`;
}

export function getId(o: { id?: string } | null | undefined): string {
  if (o?.id === undefined) throw Error('Missing ID');
  return o.id;
}

/**
 * Gets `error` message, with necessary checks that the given argument of
 * unknown type actually has .message field, as Error object would have.
 * Otherwise it falls back to "Unknown error".
 */
export function getErrorMessage(error: unknown): string {
  const message = error && typeof error === 'object' && 'message' in error
    ? error.message : undefined;

  return message && typeof message === 'string' ? message : 'Unknown error';
}

export function isInsideRect(
  point: [number, number],
  rect: DOMRect | undefined,
): boolean {
  return !!rect && point[0] >= rect.left && point[0] <= rect.right
    && point[1] >= rect.top && point[1] <= rect.bottom;
}

export function getServerServices(): null | typeof ServerServices {
  return webpack.requireWeak('server/services', import.meta.dirname);
}
